import queryString from 'query-string'

import links from '@web/libs/constants/links'
import {
  PhotoInfraThumbnail,
  PartialPhotoInfraThumbnail,
  PhotoInfraService,
  ReviewCommonThumbnailType,
  DEFAULT_PHOTOINFRA_THUMBNAILS,
  ReviewThumbnailTypes,
} from '@web/libs/media/thumbnail/thumbnail.constant'
import { neloLogger } from '@web/libs/utils'

export const photoInfraServices: PhotoInfraService[] = Object.values(PhotoInfraService)

const isServiceDomain = (serviceKey: string, url: string) => {
  // https에서 http 호출시 오류 발생 https://oss.navercorp.com/place/local/issues/1407
  if (url.startsWith('http://')) {
    return false
  }

  return [
    `ssl.phinf.net/${serviceKey}`,
    `pstatic.net/${serviceKey}`,
    `${serviceKey}.ssl.phinf.net`,
    `${serviceKey}-phinf.pstatic.net`,
    `${serviceKey}.pstatic.net`,
  ].some(pattern => url.includes(pattern))
}

export const getPhotoInfraService = (url: string): PhotoInfraService | null => {
  const service = photoInfraServices.find(service => isServiceDomain(service, url))
  return service ?? null
}

export const getCommonTypePhotoInfraUrl = (url: string, commonType: PhotoInfraThumbnail['common']) => {
  const query = queryString.stringify({
    autoRotate: true,
    type: commonType,
    src: url,
  })

  return `${links.PHOTO_INFRA_COMMON}${query}`
}

type GetPhotoInfraUrlWithTypeInputTypes = PartialPhotoInfraThumbnail | { review: ReviewCommonThumbnailType }

export const getReviewTypes = (reviewTypes: { review: ReviewCommonThumbnailType }): ReviewThumbnailTypes => ({
  'pup-review': reviewTypes.review,
  myplace: reviewTypes.review,
  video: reviewTypes.review,
})

export const shouldReturnOriginUrl = (url: string) => {
  if (url.includes('?type=')) return true

  if (url.startsWith('data:image')) return true

  return false
}

export const getPhotoInfraUrlWithType = <T extends GetPhotoInfraUrlWithTypeInputTypes = PartialPhotoInfraThumbnail>(
  url: string,
  types: T,
) => {
  if (shouldReturnOriginUrl(url)) {
    return url
  }

  const processedTypes: PartialPhotoInfraThumbnail = 'review' in types ? getReviewTypes(types) : types
  const processedTypesWithDefault = { ...DEFAULT_PHOTOINFRA_THUMBNAILS, ...processedTypes }

  const service = getPhotoInfraService(url)

  if (!service) {
    return getCommonTypePhotoInfraUrl(url, processedTypesWithDefault.common)
  }

  if (!processedTypes[service]) {
    neloLogger('warn', `default thumbnail type used: ${url} / ${JSON.stringify(types)}`)
  }

  return `${url}?type=${processedTypesWithDefault[service]}`
}
